// Filename: src/components/UnifiedCareWizard.js
// Author: Sunil Jain

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Autocomplete, CircularProgress, Typography, Box, Alert, IconButton,
    Stepper, Step, StepLabel, Checkbox, Link, FormGroup, FormControlLabel,
    Grid, Tooltip, Divider, Paper, InputAdornment, useTheme, alpha,
    List, ListItem, ListItemText, Select, MenuItem, InputLabel, FormControl, Chip, ListItemIcon
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext'; 

// Icons
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import MicIcon from '@mui/icons-material/Mic';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HealingIcon from '@mui/icons-material/Healing';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import BookIcon from '@mui/icons-material/Book';
import MoodIcon from '@mui/icons-material/Mood';
import SickIcon from '@mui/icons-material/Sick';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import QRCode from 'qrcode.react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import authFetch from '../utils/authFetch';
import useDebounce from '../hooks/useDebounce';
import logger from '../utils/logger';
import useSpeechToText from '../hooks/useSpeechToText';
import MedicationReconciliationModal from './MedicationReconciliationModal';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// --- Constants & Configurations ---
const WIZARD_STEPS = [
    'Select Patient & Plan',
    'Medications Checklist',
    'Daily Check-ins',
    'Assign Materials',
    'Assign Care Team',
    'Consent & Clinical Notes',
    'Preview & Order'
];

const FLOW_CONFIG = {
    'pre-encounter': {
        label: 'Pre-Encounter',
        types: [
            { key: 'onboarding', label: 'Pre-Op Onboarding' },
            { key: 'general', label: 'General Onboarding' },
            { key: 'custom', label: 'Custom Onboarding' },
        ]
    },
    'tcm': {
        label: 'Transitional Care (TCM)',
        types: [{ key: 'tcm', label: 'Transitional Care Management' }]
    },
    'longitudinal': {
        label: 'Longitudinal Care',
        types: [
            { key: 'rtm_musculoskeletal', label: 'RTM - Musculoskeletal' },
            { key: 'rtm_respiratory', label: 'RTM - Respiratory' },
            { key: 'ccm', label: 'Chronic Care Management (CCM)' },
            { key: 'pcm', label: 'Principal Care Management (PCM)' },
            { key: 'bhi', label: 'Behavioral Health Integration (BHI)' },
            { key: 'other', label: 'Custom Longitudinal Program' },
        ]
    }
};


const DAILY_CHECKIN_TYPES = [
    { id: 'vitals', label: 'Vitals', icon: <MonitorHeartIcon /> },
    { id: 'medication_adherence_check', label: 'Medication Log', icon: <HealingIcon /> }, // <-- FIX 1
    { id: 'mood', label: 'Mood/Feeling', icon: <MoodIcon /> },
    { id: 'symptoms_feelings', label: 'Symptoms/Pain', icon: <SickIcon /> },
    { id: 'diet_fluid', label: 'Diet/Fluids/GI', icon: <RestaurantIcon /> },
    { id: 'activity', label: 'Activity/Exertion', icon: <FitnessCenterIcon /> },
    { id: 'journal_entry', label: 'Journal', icon: <BookIcon /> }, // <-- FIX 2
];

const FREQUENCY_OPTIONS = [
    { value: 'weekly', label: 'Weekly', intervalUnit: null },
    { value: 'daily', label: 'Daily', intervalUnit: null },
    { value: 'every_x_days', label: 'Every X Days', intervalUnit: 'Days' },
    { value: 'every_x_weeks', label: 'Every X Weeks', intervalUnit: 'Weeks' },
    { value: 'once', label: 'Once Only', intervalUnit: null },
];

const sendPatientInviteAPI = async ({ tenantId, firstName, lastName, mrn, dob, email }) => {
    const payload = {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        mrn,
        date_of_birth: dob,
        tenant_id: tenantId,
    };

    if (!mrn) delete payload.mrn;
    if (!dob) delete payload.date_of_birth;

    const response = await authFetch('/users/invite-patient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    if (!response || typeof response.patient_user_id === 'undefined') {
        throw new Error(response?.detail || "Invitation request failed. The API response was invalid.");
    }
    return response;
};

const fetchTenantStaff = async ({ queryKey }) => {
    const [, tenantId] = queryKey;
    if (!tenantId) return [];
    logger.info(`UnifiedCareWizard: Fetching staff for tenant ${tenantId}`);
    try {
        const params = new URLSearchParams({ limit: '500', tenant_id: tenantId });
        return await authFetch(`/admin/users/?${params.toString()}`);
    } catch (error) {
        logger.error("UnifiedCareWizard: Error fetching tenant staff:", error);
        throw error;
    }
};
const fetchContentItems = async ({ queryKey }) => {
    const [, tenantId] = queryKey;
    if (!tenantId) return [];
    const params = new URLSearchParams({
        status: 'active',
        latest_only: 'true',
        limit: '500',
        tenant_id: tenantId
    });
    const data = await authFetch(`/content/?${params.toString()}`);
    return Array.isArray(data) ? data : [];
};
const fetchPatientMeds = ({ queryKey }) => {
    const [, { patientUserId }] = queryKey;
    if (!patientUserId) return [];
    return authFetch(`/patients/${patientUserId}/directives`);
};

const fetchMonitoringTemplateApi = async ({ queryKey }) => {
    const [, episodeType] = queryKey;
    if (!episodeType) return null;
    const data = await authFetch(`/api/monitoring-templates/default/${episodeType}`);
    return data;
};

const CARE_TEAM_ROLE_OPTIONS = [
    { value: 'primary_physician', label: 'Primary Physician' },
    { value: 'care_manager', label: 'Care Manager' },
    { value: 'clinical_staff', label: 'Clinical Staff' },
    { value: 'patient_navigator', label: 'Patient Navigator (Scheduler)' }, 
    { value: 'other', label: 'Other' },
];

// --- Main Wizard Component ---
export default function UnifiedCareWizard({ open, onClose, tenantId, initialPatientContext = null, initialAction = null }) {
    const { user: orderingClinician } = useAuth();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const isInitialPatientSet = useRef(false);
    const [lifetimeEpisodeId, setLifetimeEpisodeId] = useState(null);
    const [alertRuleOverrides, setAlertRuleOverrides] = useState({});
    const [editingRulesFor, setEditingRulesFor] = useState(null);

    // --- State Management ---
    const [activeStep, setActiveStep] = useState(0);
    const [stepError, setStepError] = useState('');
    const [draftEpisodeId, setDraftEpisodeId] = useState(null);

    // Step 0: Patient
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [searchResults, setSearchResults] = useState([]);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [showNewPatientForm, setShowNewPatientForm] = useState(false);
    const [newPatientData, setNewPatientData] = useState({ firstName: '', lastName: '', mrn: '', dob: '', email: '' });
    const [newPatientFormError, setNewPatientFormError] = useState('');
    const [isSendingInvite, setIsSendingInvite] = useState(false);
    const [provisionalPatientInviteUrl, setProvisionalPatientInviteUrl] = useState('');


    // Step 1: Initiate Care
   const [selectedProgram, setSelectedProgram] = useState(null); 

    // Step 2: Medications
    const [isMedRecModalOpen, setIsMedRecModalOpen] = useState(false);
    const [preselectedMeds, setPreselectedMeds] = useState([]);
    
    // Step 3: Daily Check-ins
    const [dailyCheckIns, setDailyCheckIns] = useState(
        DAILY_CHECKIN_TYPES.reduce((acc, checkin) => ({ ...acc, [checkin.id]: true }), {})
    );

    const handleRuleValueChange = (param, value) => {
        setAlertRuleOverrides(prev => ({
            ...prev,
            [param]: value
        }));
    };

    const fetchDefaultRules = async (checkinType) => {
        if (checkinType === 'vitals') {
            return [
                { id: 'rule1', description: 'BP Systolic >', value: 180 },
                { id: 'rule2', description: 'BP Diastolic >', value: 110 },
                { id: 'rule3', description: 'Heart Rate >', value: 120 },
            ];
        }
        return [];
    };

    // Step 4: Materials
    const [selectedContentConfigs, setSelectedContentConfigs] = useState([]);

    // Step 5: Care Team
    const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
    
    // Step 6: Consent
    const [consentState, setConsentState] = useState({
        therapyConsent: true,
        carePlanConsent: true,
        deviceSupplied: true,
        deviceEducation: true,
        revocationInfo: true,
        clinicalNotes: '',
        consentNotes: ''
    });
    
    const { isListening, transcript, startListening, stopListening, hasRecognitionSupport } = useSpeechToText({
        onResult: (command) => {
            setConsentState(p => ({ ...p, clinicalNotes: p.clinicalNotes ? `${p.clinicalNotes.trim()} ${command}` : command }));
        }
    });

    const { data: availablePrograms = [] } = useQuery({
        queryKey: ['programs', tenantId],
        queryFn: () => authFetch('/api/programs'), 
        enabled: open && !!tenantId,
    });

    const resetToManualConfiguration = () => {
        setDailyCheckIns(DAILY_CHECKIN_TYPES.reduce((acc, checkin) => ({ ...acc, [checkin.id]: true }), {}));
        setSelectedContentConfigs([]);
        setPreselectedMeds([]);
        logger.info("Care Bundle cleared. Wizard reset to default state.");
    };


    // --- Data Fetching & Mutations ---
    const { data: allTenantStaff = [] } = useQuery({ queryKey: ['tenantStaff', tenantId], queryFn: fetchTenantStaff, enabled: open && !!tenantId });
    const { data: allContentItems = [] } = useQuery({ queryKey: ['contentItems', tenantId], queryFn: fetchContentItems, enabled: open && !!tenantId });
    
    const { data: patientMeds = [], isLoading: isLoadingMeds } = useQuery({

        queryKey: ['patientMedications', { patientUserId: selectedPatient?.id }],
        queryFn: fetchPatientMeds,
        enabled: !!selectedPatient?.id && !!draftEpisodeId,
    });

    const { data: availableBundles = [], isLoading: isLoadingBundles } = useQuery({
        queryKey: ['careBundles'],
        queryFn: async () => {
            const allProtocols = await authFetch('/api/protocols/');
            return Array.isArray(allProtocols)
                ? allProtocols.filter(p => ((p.protocol_type?.toLowerCase() === 'care_bundle') || !p.protocol_type) && p.is_active)
                : [];
        },
        enabled: open,
    });

    useEffect(() => {
        if (selectedPatient && selectedPatient.patient_episodes) {
            const lifetimeEpisode = selectedPatient.patient_episodes.find(
                ep => ep.program?.key === 'lifetime_perpetual'
            );

            if (lifetimeEpisode) {
                setLifetimeEpisodeId(lifetimeEpisode.id);
                logger.info(`Located patient's lifetime episode: ${lifetimeEpisode.id}`);
            } else {
                setLifetimeEpisodeId(null);
                setStepError("Could not find the patient's master health record (lifetime episode). Please contact support.");
                logger.error(`CRITICAL: No 'lifetime_perpetual' episode found for patient ${selectedPatient.id}.`);
            }
        } else {
            setLifetimeEpisodeId(null);
        }
    }, [selectedPatient]);

    const combinedMedsForDisplay = useMemo(() => {
        const patientMedNames = new Set((patientMeds || []).map(m => m.details?.item_name.toLowerCase().trim()));
        const uniquePreselectedMeds = preselectedMeds.filter(
            pre => !patientMedNames.has(pre.details?.item_name.toLowerCase().trim())
        );
        const allMeds = [...(patientMeds || []), ...uniquePreselectedMeds];

        return allMeds.filter(item => {
                const itemType = item.item_type || item.details?.item_type;
                return (itemType === 'medication' || itemType === 'medication_adherence_check') && item.status !== 'stopped';
            });
    }, [patientMeds, preselectedMeds]);

    const draftEpisodeMutation = useMutation({
        mutationFn: (payload) => {
            if (!payload || !payload.patient_user_id || !payload.program_id) { 
                const errorMsg = "Cannot create draft plan: Required patient or program type information is missing.";
                logger.error(`UnifiedCareWizard: draftEpisodeMutation called with invalid payload.`, payload);
                return Promise.reject(new Error(errorMsg));
            }

            return authFetch('/episodes/initiate-draft', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
        },

        onSuccess: (data) => {
            if (data?.id) {
                setDraftEpisodeId(data.id);
                logger.info(`Draft episode created: ${data.id}`);
                setActiveStep(prev => prev + 1);
            } else {
                 setStepError("Failed to initiate a draft plan. The server response was invalid.");
            }
        },
        onError: (error) => setStepError(`Error initiating plan: ${error.detail || error.message || 'An unknown error occurred.'}`),
    });

    const finalizeEpisodeMutation = useMutation({
        mutationFn: ({ episodeId, payload }) => authFetch(`/api/episodes/${episodeId}/finalize`, { method: 'POST', body: payload }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboardStats', tenantId] });
            handleClose(true);
        },
        onError: (error) => setStepError(`Finalization Failed: ${error.detail || error.message || 'An unknown error occurred.'}`),
    });

    const resetWizard = useCallback(() => {
        setActiveStep(0);
        setStepError('');
        setDraftEpisodeId(null);
        setSelectedPatient(null);
        setSearchQuery('');
        setSearchResults([]);
        setIsLoadingSearch(false);
        setSearchError('');
        setShowNewPatientForm(false);
        setNewPatientData({ firstName: '', lastName: '', mrn: '', dob: '', email: '' });
        setNewPatientFormError('');
        setIsSendingInvite(false);
        setProvisionalPatientInviteUrl('');

        setSelectedProgram(null); 
        setIsMedRecModalOpen(false);
        setDailyCheckIns(
            DAILY_CHECKIN_TYPES.reduce((acc, checkin) => ({ ...acc, [checkin.id]: true }), {})
        );

        setSelectedContentConfigs([]);
        setSelectedTeamMembers([]);

        setConsentState({
            therapyConsent: true,
            carePlanConsent: true,
            deviceSupplied: true,
            deviceEducation: true,
            revocationInfo: true,
            clinicalNotes: '',
            consentNotes: ''
        });
        isInitialPatientSet.current = false;
        logger.info("UnifiedCareWizard state has been reset.");
    }, []);

    const handleClose = (didSubmit = false) => { resetWizard(); onClose(didSubmit); };
    
    const fetchPatients = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }
        setIsLoadingSearch(true);
        setSearchError('');
        try {
            const params = new URLSearchParams({ role: 'patient', limit: '10', search: query, tenant_id: tenantId });
            const data = await authFetch(`/admin/users/?${params.toString()}`);
            setSearchResults(Array.isArray(data) ? data : []);
        } catch (e) {
            setSearchError(e.message || 'Failed to search for patients.');
            setSearchResults([]);
        } finally {
            setIsLoadingSearch(false);
        }
    }, [tenantId]);

    const prevPatientId = usePrevious(selectedPatient?.id);
    useEffect(() => {
        // This condition is true only when the patient ID changes from one valid ID to another.
        if (prevPatientId && selectedPatient?.id && prevPatientId !== selectedPatient.id) {
            logger.info("UnifiedCareWizard: Patient context changed. Resetting plan configuration.");
            setSelectedProgram(null);
            setDailyCheckIns(DAILY_CHECKIN_TYPES.reduce((acc, checkin) => ({ ...acc, [checkin.id]: true }), {}));
            setSelectedContentConfigs([]);
            setPreselectedMeds([]);
            setStepError('');
        }
    }, [selectedPatient?.id, prevPatientId]); // Add prevPatientId to dependency array


    useEffect(() => { if (debouncedSearchQuery) fetchPatients(debouncedSearchQuery); }, [debouncedSearchQuery, fetchPatients]);
    
    const handlePatientSelect = (event, newValue) => { if(newValue) { setSelectedPatient(newValue); setStepError(''); }};
    

    const handleRegisterPatient = async () => {
        const { firstName, lastName, mrn, dob, email } = newPatientData;
        if (!firstName.trim() || !email.trim() || !/\S+@\S+\.\S+/.test(email)) {
            setNewPatientFormError("First Name and a valid Email are required.");
            return;
        }

        setIsSendingInvite(true);
        setNewPatientFormError('');

        try {
            const searchParams = new URLSearchParams({ role: 'patient', limit: '5', search: email.trim(), tenant_id: tenantId });
            const existingPatients = await authFetch(`/admin/users/?${searchParams.toString()}`);
            if (Array.isArray(existingPatients) && existingPatients.length > 0) {
                setNewPatientFormError("A patient with this email already exists. Please select them from the search results.");
                setSearchResults(existingPatients); 
                setShowNewPatientForm(false); 
                setIsSendingInvite(false);
                return;
            }
            const response = await sendPatientInviteAPI({ tenantId, ...newPatientData });

            if (response.is_already_in_tenant) {
                setNewPatientFormError(`Patient (${newPatientData.email}) is already an active member. Please search for them.`);
                return;
            }

            const newSelectedPatient = {
                id: response.patient_user_id,
                name: `${firstName.trim()} ${lastName.trim() || ''}`.trim(),
                isProvisional: true,
            };

            setSelectedPatient(newSelectedPatient);

            if (response.registration_url) {
                setProvisionalPatientInviteUrl(response.registration_url);
            } else {
                logger.error("Patient invite API did not return a registration_url.");
                setNewPatientFormError("Patient was created, but the registration link could not be generated. Please contact support.");
            }

            setSelectedPatient(newSelectedPatient);

            if (response.registration_url) {
                setProvisionalPatientInviteUrl(response.registration_url);

            } else {
                logger.error("Patient invite API did not return a registration_url.");
                setNewPatientFormError("Patient was created, but the registration link could not be generated. Please contact support.");
            }

        } catch (error) {
            logger.error("Failed to register patient:", error);
            setNewPatientFormError(error.detail || error.message || "An unexpected error occurred during patient registration.");
        } finally {
            setIsSendingInvite(false);
        }
    };

    const handleNext = () => {
        setStepError('');
        const currentStepName = WIZARD_STEPS[activeStep];

        if (currentStepName === 'Select Patient & Plan') {
            if (!selectedPatient || !selectedProgram) {
                setStepError("Please select a patient and a clinical program to continue.");
                return;
            }
            draftEpisodeMutation.mutate({
                patient_user_id: selectedPatient.id,
                program_id: selectedProgram.id,
                start_date: new Date().toISOString().split('T')[0],
                diagnosis_codes: [{ code: '', display: selectedProgram.name }]
            });
        } else if (currentStepName === 'Preview & Order') {
            if (finalizeEpisodeMutation.isPending || !draftEpisodeId) {
                setStepError("Cannot finalize: The draft plan ID is missing or finalization is already in progress.");
                return;
            }
            
            const newMedItems = combinedMedsForDisplay
                .filter(med => med.isNew || med.isDefault)
                .map(med => {
                    const details = med.details || {};
                    const metadata = details.item_metadata || {};
                    const schedule = details.tapering_schedule || metadata.tapering_schedule;

                    if (metadata.tapering_schedule) {
                        delete metadata.tapering_schedule;
                    }

                    return {
                        item_name: details.item_name || med.item_name,
                        item_type: 'medication',
                        frequency_type: details.frequency_type || 'as_needed',
                        times_of_day: details.times_of_day || [],
                        tapering_schedule: schedule, 
                        regimen_start_date: med.start_date || new Date().toISOString().split('T')[0],
                        reminders_active: details.reminders_active || true,
                        item_metadata: metadata || { source: 'VERIFIED_IN_WIZARD' }
                    };
                });

            const checkinItems = Object.keys(dailyCheckIns)
                .filter(key => dailyCheckIns[key])
                .map(checkinId => ({
                    item_name: `Daily Check-in: ${DAILY_CHECKIN_TYPES.find(c => c.id === checkinId)?.label || checkinId}`,
                    item_type: checkinId,
                    frequency_type: 'daily',
                    times_of_day: ["09:00"],
                    regimen_start_date: new Date().toISOString().split('T')[0],
                }));
            const contentItems = selectedContentConfigs.map(config => ({
                item_name: config.title,
                item_type: config.content_type,
                item_metadata: { 
                    content_item_id: config.id,
                    program_phase_tag: config._program_phase || 'pre_operative' // Send our phase tag
                },
                regimen_start_date: config.due_date || new Date().toISOString().split('T')[0],
                frequency_type: 'once',
            }));
            const finalPayload = {
                status: 'active',
                regimen_items: [...newMedItems, ...checkinItems, ...contentItems],
                care_team_members: selectedTeamMembers.map(member => ({ 
                    user_id: member.user.id, 
                    role: member.role 
                })),
                care_plan_details: { 
                    clinical_notes: consentState.clinicalNotes.trim(), 
                    device_supplied: consentState.deviceSupplied, 
                    device_education_completed: consentState.deviceEducation 
                },
            };
            finalizeEpisodeMutation.mutate({ episodeId: draftEpisodeId, payload: finalPayload });

        } else {
            if (activeStep < WIZARD_STEPS.length - 1) {
                setActiveStep(prev => prev + 1);
            }
        }
    };

    const handleBack = () => { setStepError(''); if (activeStep > 0) setActiveStep(prev => prev - 1); };
    
    const getReviewSummaryText = () => {
        let summary = `CARE PLAN SUMMARY\n`;
        summary += `====================================\n`;
        summary += `SaMD Episode ID: ${draftEpisodeId || 'Pending finalization...'}\n`;
        summary += `Patient: ${selectedPatient?.name || 'N/A'}\n`;
        if (selectedPatient?.isProvisional) summary += `Status: New Patient Invite\n`;
        
        const program = selectedProgram;
        summary += `Program: ${selectedProgram?.name || 'N/A'}\n`;
        summary += `Diagnosis: ${selectedProgram?.name || 'N/A'}\n`;
        summary += `====================================\n\n`;

        const enabledCheckins = Object.keys(dailyCheckIns).filter(key => dailyCheckIns[key]);
        summary += `--- Assigned Daily Check-ins (${enabledCheckins.length}) ---\n`;
        if (enabledCheckins.length > 0) {
            enabledCheckins.forEach(key => {
                const checkinLabel = DAILY_CHECKIN_TYPES.find(c => c.id === key)?.label || key;
                summary += `- ${checkinLabel}\n`;
            });
        } else {
            summary += `No daily check-ins were assigned.\n`;
        }
        
        summary += `\n--- Assigned Materials (${selectedContentConfigs.length}) ---\n`;
        if (selectedContentConfigs.length > 0) {
            selectedContentConfigs.forEach((item, idx) => {
                summary += `${idx + 1}. ${item.title}\n   (Due: ${item.due_date ? new Date(item.due_date).toLocaleDateString() : 'N/A'}, Reminder: ${item.reminder_frequency})\n`;
            });
        } else {
            summary += `No materials were assigned.\n`;
        }

        summary += `\n--- Assigned Care Team (${selectedTeamMembers.length}) ---\n`;
        if (selectedTeamMembers.length > 0) {
            selectedTeamMembers.forEach(staff => {
                const roleLabel = staff.team_member_profile?.role_label;
                const rawRole = staff.role;
                const formattedRole = rawRole ? rawRole.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A';
                const role = roleLabel || formattedRole;
                summary += `- ${staff.first_name||''} ${staff.last_name||''} (${role})\n`
            });
        } else {
            summary += `No care team members were assigned.\n`;
        }
        
        summary += `\n--- Consent & Clinical Notes ---\n`;
        summary += `Verbal consent for program: ${consentState.therapyConsent ? 'Yes' : 'No'}\n`;
        summary += `Care plan established and shared: ${consentState.carePlanConsent ? 'Yes' : 'No'}\n`;
        summary += `Informed of right to revoke consent: ${consentState.revocationInfo ? 'Yes' : 'No'}\n`;
        summary += `Required device(s) supplied: ${consentState.deviceSupplied ? 'Yes' : 'No'}\n`;
        summary += `Patient educated on device use: ${consentState.deviceEducation ? 'Yes' : 'No'}\n`;
        summary += `\nClinical Notes:\n${consentState.clinicalNotes.trim() || '(No clinical notes were entered.)'}\n`;
        if (consentState.consentNotes.trim()) {
            summary += `\nConsent Notes:\n${consentState.consentNotes.trim()}\n`;
        }
        
        if (selectedPatient?.isProvisional && provisionalPatientInviteUrl) {
            summary += `\n--- New Patient Registration Details ---\n`;
            summary += `This link can be sent to the patient to complete their registration.\n`;
            summary += `Registration Link: ${provisionalPatientInviteUrl}\n`;
        }

        return summary;
    };
    
    const handleDownloadPdf = () => {
        const input = document.getElementById('printable-summary');
        if (!input) return;
        html2canvas(input, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'p', unit: 'px', format: [canvas.width, canvas.height] });
            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(`CarePlan_Summary_${selectedPatient?.name?.replace(/\s/g, '_') || 'Patient'}.pdf`);
        });
    };

    const renderStepContent = () => {
        const currentStepName = WIZARD_STEPS[activeStep];
        console.log(`[WIZARD DEBUG] renderStepContent called for step: "${currentStepName}"`); // <<< ADD THIS LINE

        switch (currentStepName) {

            case 'Select Patient & Plan':
                console.log('[WIZARD DEBUG] Executing case: "Select Patient & Plan"'); // <<< ADD THIS LINE
                return (
                    <Grid container spacing={4} sx={{ pt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>1. Find or Add Patient</Typography>
                            {showNewPatientForm ? (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={provisionalPatientInviteUrl ? 7 : 12}>
                                        <Typography gutterBottom variant="h6">Add New Patient</Typography>
                                        <TextField label="First Name *" name="firstName" value={newPatientData.firstName} onChange={(e) => setNewPatientData(p => ({ ...p, firstName: e.target.value }))} fullWidth margin="dense" required />
                                        <TextField label="Last Name" name="lastName" value={newPatientData.lastName} onChange={(e) => setNewPatientData(p => ({ ...p, lastName: e.target.value }))} fullWidth margin="dense" />
                                        <TextField label="MRN" name="mrn" value={newPatientData.mrn} onChange={(e) => setNewPatientData(p => ({ ...p, mrn: e.target.value }))} fullWidth margin="dense" helperText="Medical Record Number for this patient."/>
                                        <TextField label="Date of Birth" name="dob" type="date" value={newPatientData.dob} onChange={(e) => setNewPatientData(p => ({ ...p, dob: e.target.value }))} fullWidth margin="dense" InputLabelProps={{ shrink: true }} helperText="Required if MRN is not provided." />
                                        <TextField label="Email Address *" name="email" type="email" value={newPatientData.email} onChange={(e) => setNewPatientData(p => ({ ...p, email: e.target.value }))} fullWidth margin="dense" required />
                                        {newPatientFormError && <Alert severity="error" sx={{ mt: 1.5 }}>{newPatientFormError}</Alert>}
                                        <Button variant="contained" onClick={handleRegisterPatient} disabled={isSendingInvite} fullWidth sx={{ mt: 2 }}>
                                            {isSendingInvite ? <CircularProgress size={24} /> : "Register & Create Invite"}
                                        </Button>
                                        <Button variant="text" size="small" onClick={() => {setShowNewPatientForm(false); setProvisionalPatientInviteUrl('');}} sx={{mt:1}}>Back to Search</Button>
                                    </Grid>
                                    {provisionalPatientInviteUrl && (
                                        <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Paper elevation={2} sx={{p:2, textAlign: 'center', width: '100%'}}>
                                                <Typography variant="subtitle1" gutterBottom>Patient Registration</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>Patient can scan this code to complete registration.</Typography>
                                                <QRCode value={provisionalPatientInviteUrl} size={160} />
                                                <Link href={provisionalPatientInviteUrl} target="_blank" sx={{ display: 'block', mt:1.5, wordBreak: 'break-all', fontSize: '0.8rem' }}>Registration Link</Link>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            ) : (
                                <>
                                    <Typography>Find an existing patient or add a new one to begin.</Typography>
                                    <Autocomplete
                                        options={searchResults}
                                        getOptionLabel={(o) => `${o.last_name || ''}, ${o.first_name || ''} (${o.mrn || o.email || 'N/A'})`}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        filterOptions={x => x}
                                        loading={isLoadingSearch}
                                        value={selectedPatient}
                                        onChange={handlePatientSelect}
                                        onInputChange={(e, val) => setSearchQuery(val)}
                                        renderInput={(params) => <TextField {...params} label="Search Patient by Name, MRN, or Email" fullWidth autoFocus sx={{mt:2}} InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><PersonSearchIcon /></InputAdornment>}} />}
                                    />
                                    {searchError && <Alert severity="warning" sx={{ my: 1.5 }}>{searchError}</Alert>}
                                    <Divider sx={{my:2}}>OR</Divider>
                                    <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={() => setShowNewPatientForm(true)}>Add New Patient</Button>
                                </>
                            )}
                        </Grid>       
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>2. Select Clinical Program</Typography>
                            <Autocomplete
                                options={availablePrograms}
                                getOptionLabel={(program) => program.name || "Unnamed Program"}
                                

                                value={selectedProgram}
                                // --- START OF MODIFICATION: Replace the entire onChange handler ---
                                onChange={(event, newProgram) => {
                                    setSelectedProgram(newProgram);

                                    if (!newProgram) {
                                        resetToManualConfiguration();
                                        return;
                                    }

                                    // Inside UnifiedCareWizard.js -> renderStepContent -> case 'Select Patient & Plan' -> Autocomplete onChange

                                    // This function populates the wizard state from a program object
                                    const populateStateFromProgram = (program) => {
                                        logger.info(`Populating wizard state from Program: ${program.name}`);
                                        
                                        let allContentConfigs = [];
                                        let allMedDirectives = [];
                                        
                                        // FIX START: Initialize check-ins to all FALSE first. 
                                        // We will turn them TRUE only if the bundle mandates them.
                                        let checkinState = DAILY_CHECKIN_TYPES.reduce((acc, c) => ({ ...acc, [c.id]: false }), {});
                                        // FIX END
                                        
                                        let programPhases = [];

                                        // ... (Existing logic to determine programPhases based on new vs old definition remains the same) ...
                                        if (program.phases_definition && program.phases_definition.length > 0) {
                                            programPhases = program.phases_definition;
                                        } else {
                                            // ... (Existing fallback logic) ...
                                            const staticPhaseMap = [
                                                { key: 'onboarding', bundle_id: program.bundle_onboarding_id, tag: 'pre_operative' },
                                                { key: 'pre_surgery', bundle_id: program.bundle_pre_surgery_id, tag: 'pre_operative' },
                                                { key: 'day_of_surgery', bundle_id: program.bundle_day_of_surgery_id, tag: 'day_of_surgery' },
                                                { key: 'post_surgery', bundle_id: program.bundle_post_surgery_id, tag: 'post_operative' },
                                                { key: 'longitudinal', bundle_id: program.bundle_longitudinal_id, tag: 'longitudinal' },
                                            ];
                                            programPhases = staticPhaseMap
                                                .filter(p => p.bundle_id)
                                                .map(p => ({
                                                    key: p.key,
                                                    label: p.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                                    bundle_id: p.bundle_id,
                                                    _program_phase_tag: p.tag
                                                }));
                                        }

                                        // Iterate over phases to extract data
                                        programPhases.forEach(phase => {
                                            const bundleId = phase.bundle_id;
                                            if (!bundleId) return;

                                            const bundleData = availableBundles.find(b => b.id === bundleId);
                                            if (!bundleData) return;
                                            
                                            const def = bundleData.module_definition || {};

                                            // 1. Content Items (Existing logic)
                                            const contentConfigs = def.default_content_configs || [];
                                            const contentItemsFromBundle = contentConfigs.map(config => {
                                                const fullItem = allContentItems.find(item => item.id === config.content_item_id);
                                                if (!fullItem) return null;
                                                const dueDate = new Date();
                                                dueDate.setDate(dueDate.getDate() + (config.default_due_in_days || 7));
                                                return {
                                                    ...fullItem,
                                                    due_date: dueDate.toISOString().split('T')[0],
                                                    reminder_frequency: config.default_frequency || 'weekly',
                                                    frequency_interval: 1,
                                                    _program_phase: phase.key 
                                                };
                                            }).filter(Boolean);
                                            allContentConfigs.push(...contentItemsFromBundle);

                                            // 2. Medications (Existing logic)
                                            const bundleMeds = (def.medication_directives || []).map(medDef => ({
                                                id: `bundle-med-${medDef.item_name || medDef.med_name}`,
                                                details: {
                                                    item_name: medDef.item_name || medDef.med_name,
                                                    item_type: 'medication',
                                                    frequency_type: 'tapering_schedule',
                                                    item_metadata: medDef.item_metadata,
                                                    tapering_schedule: medDef.tapering_schedule 
                                                },
                                                status: 'active',
                                                isDefault: true,
                                                is_critical: medDef.is_critical || false
                                            }));
                                            allMedDirectives.push(...bundleMeds);

                                            // FIX START: 3. Process Daily Check-ins
                                            // We aggregate check-ins from ALL phases. If ANY phase requires it, it gets turned on.
                                            const mandates = def.mandated_daily_checkins || [];
                                            if (Array.isArray(mandates)) {
                                                mandates.forEach(checkinId => {
                                                    if (checkinState.hasOwnProperty(checkinId)) {
                                                        checkinState[checkinId] = true;
                                                    }
                                                });
                                            }
                                            // FIX END
                                        });

                                        // 4. Set state
                                        setSelectedContentConfigs(allContentConfigs);
                                        setPreselectedMeds(allMedDirectives);
                                        setDailyCheckIns(checkinState); // <--- This now reflects the bundle's mandates
                                    };
                                    
                                    // Run the population logic
                                    populateStateFromProgram(newProgram);

                                }}
                                // --- END OF MODIFICATION ---
                                renderInput={(params) => (

                                    <TextField 
                                        {...params} 
                                        label="Search for a Clinical Program"
                                        helperText="This will define the patient's primary care journey."
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                ); 
                
            case 'Medications Checklist': 
                console.log('[WIZARD DEBUG] Executing case: "Medications Checklist"');
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Medications Checklist</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Review the patient's reconciled medication list. Use the button to open the full editor to make changes.
                        </Typography>
                        <Button variant="contained" startIcon={<LocalHospitalIcon />} onClick={() => setIsMedRecModalOpen(true)}>
                            Manage Medication List
                        </Button>
                        
                        <Paper variant="outlined" sx={{ p: 2, mt: 2, maxHeight: '45vh', overflowY: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" component="div">Current Reconciled Medications</Typography>
                                {isLoadingMeds && <CircularProgress size={20} sx={{ ml: 2 }} />}
                            </Box>
                            {!isLoadingMeds && combinedMedsForDisplay.length === 0 && <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>No medications on record for this patient.</Typography>}
                            
                            {!isLoadingMeds && combinedMedsForDisplay.length > 0 && (
                                <List dense>
                                    {combinedMedsForDisplay.map(med => (
                                        
                                        <ListItem key={med.id} sx={{ pl: 0 }}>
                                            {med.isDefault && <Chip label="Default" size="small" variant="outlined" color="info" sx={{mr: 1}} />}
                                            {med.is_critical && <Chip icon={<ReportProblemIcon />} label="Critical" color="error" size="small" variant="outlined" sx={{mr: 2}} />}
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                        
                                                {med.status === 'active' ?
                                                    <Tooltip title="Approved"><CheckCircleIcon color="success" fontSize="small" /></Tooltip> :
                                                    <Tooltip title="Pending Review"><PendingActionsIcon color="warning" fontSize="small" /></Tooltip>
                                                }
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={(med.details?.item_name || med.item_name) || 'Unnamed Medication'}
                                                secondary={`${(med.details?.item_metadata?.dosage || '')} - ${(med.details?.item_metadata?.frequency_instruction || '')}`} 
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>


                    </Box>
                );

            case 'Daily Check-ins': // <<< ADD THIS CASE WITH A LOGGER
                console.log('[WIZARD DEBUG] Executing case: "Daily Check-ins"');
                const handleCheckinChange = (checkinId) => {
                    setDailyCheckIns(prev => ({ ...prev, [checkinId]: !prev[checkinId] }));
                };
                const renderRuleEditor = (checkin) => {
                    const MOCK_RULES = {
                        vitals: [
                            { id: 'vitals_sys', param: 'bp_systolic', description: 'BP Systolic >', value: '180' },
                            { id: 'vitals_dia', param: 'bp_diastolic', description: 'BP Diastolic >', value: '110' },
                            { id: 'vitals_hr', param: 'heart_rate', description: 'Heart Rate >', value: '120' },
                        ],
                        symptoms_feelings: [
                            { id: 'symptoms_pain', param: 'pain_level', description: 'Pain Level >', value: '8' },
                        ],
                        mood: [
                        ]
                    };

                    const rules = (editingRulesFor === checkin.id) ? (MOCK_RULES[checkin.id] || []) : [];

                    if (!rules.length) {
                        if (editingRulesFor === checkin.id) {
                            return (
                                <Paper elevation={2} sx={{ p: 2, my: 1, ml: 4, bgcolor: alpha(theme.palette.grey[200], 0.4) }}>
                                    <Typography variant="caption" color="text.secondary">No real-time threshold rules are applicable for this check-in type.</Typography>
                                </Paper>
                            );
                        }
                        return null;
                    }

                    return (
                        <Paper elevation={2} sx={{ p: 2, my: 1, ml: 4, bgcolor: alpha(theme.palette.primary.light, 0.1) }}>
                            <Typography variant="subtitle2" sx={{mb: 1}}>Customize Real-Time Alert Thresholds</Typography>
                            {rules.map(rule => (
                                <Box key={rule.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="body2" sx={{flex: 1}}>{rule.description}</Typography>
                                    <TextField
                                        size="small"
                                        sx={{width: '100px'}}
                                        value={alertRuleOverrides[rule.param] ?? rule.value}
                                        onChange={(e) => handleRuleValueChange(rule.param, e.target.value)}
                                    />
                                </Box>
                            ))}
                        </Paper>
                    );
                };

                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Assign Daily Check-in Tasks</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Select monitoring tasks. Click the alert icon to view and customize patient-specific thresholds.
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <FormGroup>
                                {DAILY_CHECKIN_TYPES.map(checkin => (
                                    <React.Fragment key={checkin.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={<Checkbox checked={!!dailyCheckIns[checkin.id]} onChange={() => handleCheckinChange(checkin.id)} />}
                                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}>{checkin.icon}&nbsp;<Typography>{checkin.label}</Typography></Box>}
                                            />
                                            {dailyCheckIns[checkin.id] && (
                                                <Tooltip title="Edit Alert Rules">
                                                    <IconButton size="small" onClick={() => setEditingRulesFor(prev => prev === checkin.id ? null : checkin.id)}>
                                                        <EditNotificationsIcon color={editingRulesFor === checkin.id ? "primary" : "action"} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        {editingRulesFor === checkin.id && renderRuleEditor(checkin)}
                                    </React.Fragment>
                                ))}
                            </FormGroup>
                        </Paper>
                    </Box>
                );


            case 'Assign Materials': // <<< ADD THIS CASE WITH A LOGGER
                console.log('[WIZARD DEBUG] Executing case: "Assign Materials"');
                const handleContentConfigChange = (itemId, field, value) => {
                    setSelectedContentConfigs(prevConfigs =>
                        prevConfigs.map(config =>
                            config.id === itemId ? { ...config, [field]: value } : config
                        )
                    );
                };
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Assign Materials & Activities</Typography>
                        <Autocomplete
                            multiple
                            fullWidth
                            options={allContentItems}
                            getOptionLabel={o => o.title || ''}
                            value={selectedContentConfigs}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        
                            onChange={(e, newValues) => {
                                const configuredValues = newValues.map(item => {
                                    const existingConfig = selectedContentConfigs.find(c => c.id === item.id);
                                    if (existingConfig) {
                                        return existingConfig;
                                    }
                                    const defaultDueDate = new Date();
                                    defaultDueDate.setDate(defaultDueDate.getDate() + 5);
                                    const formattedDueDate = defaultDueDate.toISOString().split('T')[0];
                                    return {
                                        ...item,                 // Use 'item', not 'fullItem'
                                        due_date: formattedDueDate, // Use 'formattedDueDate'
                                        reminder_frequency: 'weekly', // Set a default
                                        frequency_interval: 1,
                                        is_critical: false            // Set a default
                                    };
                                });
                                setSelectedContentConfigs(configuredValues);
                            }}

                            renderInput={(params) => <TextField {...params} label="Search Content Studio..." />}
                        />
                        <Divider sx={{ my: 2 }} />
                        {selectedContentConfigs.length > 0 && (
                            <List>
                                {[...selectedContentConfigs].reverse().map((config) => (
                                    <ListItem key={config.id} divider>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={5}>

                                                <ListItemText 
                                                    primary={config.title} 
                                                    secondary={
                                                        <Box component="span" sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
                                                            <Typography component="span" variant="body2" color="text.secondary">
                                                                {config.content_type}
                                                            </Typography>
                                                            {config.is_critical && <Chip icon={<ReportProblemIcon />} label="Critical" color="error" size="small" variant="outlined" />}
                                                            {config.content_type === 'attestation_request' && <Chip icon={<CheckCircleIcon />} label="Attestation" color="info" size="small" variant="outlined" />}
                                                        </Box>
                                                    } 
                                                />

                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Due Date (Optional)"
                                                    type="date"
                                                    value={config.due_date}
                                                    onChange={(e) => handleContentConfigChange(config.id, 'due_date', e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>


                                            <Grid item xs={12} sm={3}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Frequency</InputLabel>
                                                        <Select
                                                            label="Frequency"
                                                            value={config.reminder_frequency}
                                                            onChange={(e) => handleContentConfigChange(config.id, 'reminder_frequency', e.target.value)}
                                                        >
                                                            {FREQUENCY_OPTIONS.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    {FREQUENCY_OPTIONS.find(o => o.value === config.reminder_frequency)?.intervalUnit && (
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            label={FREQUENCY_OPTIONS.find(o => o.value === config.reminder_frequency)?.intervalUnit}
                                                            value={config.frequency_interval || 1}
                                                            onChange={(e) => handleContentConfigChange(config.id, 'frequency_interval', e.target.value)}
                                                            sx={{ width: '100px' }}
                                                            inputProps={{ min: 1 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Grid>


                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                );

            case 'Assign Care Team':
                console.log('[WIZARD DEBUG] Executing case: "Assign Care Team"'); // <<< ADD THIS LINE

                console.log('[WIZARD DEBUG] Declaring "availableSchedulers"...'); // <<< ADD THIS LINE
                const availableSchedulers = allTenantStaff.filter(staff =>
                    staff.team_member_profile?.title?.toLowerCase() === 'scheduler'
                );
                console.log('[WIZARD DEBUG] Declared "availableSchedulers".'); // <<< ADD THIS LINE

                console.log('[WIZARD DEBUG] Declaring "selectedSchedulerMember"...'); // <<< ADD THIS LINE
                const selectedSchedulerMember = selectedTeamMembers.find(m => m.role === 'patient_navigator');
                console.log('[WIZARD DEBUG] Declared "selectedSchedulerMember".'); // <<< ADD THIS LINE

                console.log('[WIZARD DEBUG] Declaring "selectedSchedulerId"...'); // <<< ADD THIS LINE
                const selectedSchedulerId = selectedSchedulerMember?.user?.id;
                console.log('[WIZARD DEBUG] Declared "selectedSchedulerId".'); // <<< ADD THIS LINE

                console.log('[WIZARD DEBUG] Declaring "otherTeamMembers"...'); // <<< ADD THIS LINE
                const otherTeamMembers = selectedTeamMembers.filter(m => m.role !== 'patient_navigator');
                console.log('[WIZARD DEBUG] Declared "otherTeamMembers".'); // <<< ADD THIS LINE

                console.log('[WIZARD DEBUG] Declaring "otherStaffOptions"...'); // <<< ADD THIS LINE
                const otherStaffOptions = allTenantStaff.filter(staff =>
                    staff.id !== selectedSchedulerId
                );
                console.log('[WIZARD DEBUG] Declared "otherStaffOptions". About to return JSX.'); // <<< ADD THIS LINE

                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Assign Care Team</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            A default scheduler has been selected based on their profile title. You can change this for this specific episode if needed.
                        </Typography>

                        <Autocomplete
                            options={availableSchedulers.length > 0 ? availableSchedulers : [orderingClinician]}
                            getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.team_member_profile?.title || option.role.replace(/_/g, ' ')})`}
                            value={selectedSchedulerMember?.user || null}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(event, newUser) => {
                                const otherMembersOnly = selectedTeamMembers.filter(m => m.role !== 'patient_navigator');
                                if (newUser) {
                                    setSelectedTeamMembers([{ user: newUser, role: 'patient_navigator' }, ...otherMembersOnly]);
                                } else {
                                    setSelectedTeamMembers(otherMembersOnly);
                                }
                            }}
                            renderInput={(params) => <TextField {...params} label="Patient Navigator (Scheduler) *" required />}
                            sx={{ mb: 3 }}
                        />

                        <Autocomplete
                            multiple
                            options={otherStaffOptions}
                            getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''}`}
                            value={otherTeamMembers.map(m => m.user)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(event, newUsers) => {
                                const navigator = selectedTeamMembers.find(m => m.role === 'patient_navigator');
                                const newOtherMembers = newUsers.map(user => ({ user, role: 'clinical_staff' }));
                                setSelectedTeamMembers(navigator ? [navigator, ...newOtherMembers] : newOtherMembers);
                            }}
                            renderInput={(params) => <TextField {...params} label="Add Other Team Members (Optional)" />}
                        />
                    </Box>
                );
                // --- END OF REPLACEMENT LOGIC ---

            case 'Consent & Clinical Notes':
                const handleConsentStateChange = (e) => setConsentState(p => ({ ...p, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Consent & Clinical Notes</Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Billing & Consent Checklist *</Typography>
                            <FormGroup>
                                <FormControlLabel name="therapyConsent" control={<Checkbox checked={consentState.therapyConsent} onChange={handleConsentStateChange} />} label="Patient provided verbal consent for program, including services and cost-sharing." />
                                <FormControlLabel name="carePlanConsent" control={<Checkbox checked={consentState.carePlanConsent} onChange={handleConsentStateChange} />} label="A comprehensive care plan has been established and shared." />
                                <FormControlLabel name="revocationInfo" control={<Checkbox checked={consentState.revocationInfo} onChange={handleConsentStateChange} />} label="Patient was informed they can revoke consent at any time." />
                                <FormControlLabel name="deviceSupplied" control={<Checkbox checked={consentState.deviceSupplied} onChange={handleConsentStateChange} />} label="Required medical device(s) have been supplied." />
                                <FormControlLabel name="deviceEducation" control={<Checkbox checked={consentState.deviceEducation} onChange={handleConsentStateChange} />} label="Patient has been educated on the use of medical device(s)." />
                            </FormGroup>
                        </Paper>
                        <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Clinical Notes *</Typography>
                            <TextField fullWidth multiline rows={6} name="clinicalNotes" value={consentState.clinicalNotes} onChange={handleConsentStateChange} placeholder="Add notes on clinical reasoning, patient's condition, etc..."
                                InputProps={{ endAdornment: hasRecognitionSupport && <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}><Tooltip title="Start/Stop Dictation"><IconButton onClick={isListening ? stopListening : startListening} color={isListening ? 'error' : 'primary'}><MicIcon /></IconButton></Tooltip></InputAdornment> }} />
                        </Paper>
                    </Box>
                );
            case 'Preview & Order':
                const summaryText = getReviewSummaryText();
                return (
                    <Box>
                        <Typography variant="h5" gutterBottom>Review & Order Plan</Typography>
                        {/* Wrapper Div for PDF generation */}
                        <Box id="printable-summary" sx={{p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
                            {selectedPatient?.isProvisional && provisionalPatientInviteUrl && (
                                <Paper elevation={0} sx={{p:2, textAlign: 'center', width: 'fit-content', mx: 'auto', my: 2}}>
                                    <Typography variant="subtitle1" gutterBottom>New Patient Registration</Typography>
                                    <QRCode value={provisionalPatientInviteUrl} size={140} />
                                    <Typography variant="caption" display="block" sx={{mt:1}}>Patient can scan to complete registration.</Typography>
                                </Paper>
                            )}
                            <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem', maxHeight: '50vh', overflowY: 'auto'}}>{summaryText}</Typography>
                        </Box>

                        <Box sx={{display:'flex', gap:1, mt: 2}}>
                             <Button startIcon={<ContentCopyIcon />} onClick={() => navigator.clipboard.writeText(summaryText)}>Copy Text</Button>
                             <Button startIcon={<DownloadIcon />} onClick={handleDownloadPdf}>Download as PDF</Button>
                        </Box>
                        <Alert severity="info" sx={{mt:2}}>Ensure all details are correct before placing the final order.</Alert>
                    </Box>
                );

            default:
                return <Typography>Unknown step</Typography>;
        }
    };


    return (
        <>
            <Dialog open={open} onClose={() => handleClose(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '90vh', maxHeight: '90vh' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Unified Care Wizard: {selectedPatient?.name || '...'}
                    <IconButton onClick={() => handleClose(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                    {WIZARD_STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                </Stepper>
                <DialogContent dividers sx={{ p: 3, bgcolor: 'grey.50' }}>
                    {stepError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStepError('')}>{stepError}</Alert>}
                    {draftEpisodeMutation.isPending ? <Box sx={{textAlign: 'center', p:4}}><CircularProgress /><Typography sx={{mt:2}}>Initiating secure plan...</Typography></Box> : renderStepContent()}
                </DialogContent>
                <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 1.5 }}>
                    <Button onClick={() => handleClose(false)} disabled={draftEpisodeMutation.isPending || finalizeEpisodeMutation.isPending}>Cancel</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={handleBack} disabled={activeStep === 0 || draftEpisodeMutation.isPending || finalizeEpisodeMutation.isPending}>Back</Button>
                    <Button
                        onClick={handleNext}
                        variant="contained"
                        disabled={draftEpisodeMutation.isPending || finalizeEpisodeMutation.isPending}
                        sx={{ minWidth: '120px' }}
                    >
                        {finalizeEpisodeMutation.isPending
                            ? <CircularProgress size={24} />
                            : (activeStep === WIZARD_STEPS.length - 1 ? 'Order/Deliver Plan' : 'Next')}
                    </Button>
                </DialogActions>
            </Dialog>

            {isMedRecModalOpen && (
            <MedicationReconciliationModal
                open={isMedRecModalOpen}
                onClose={() => {
                    setIsMedRecModalOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['patientMedications', { patientUserId: selectedPatient?.id }] });
                }}
                patientUserId={selectedPatient?.id}
                episodeId={draftEpisodeId}
                patientName={selectedPatient?.name}
                showSnackbar={setStepError}
                tenantId={tenantId}
            />
            )}
        </>
    );
}
